import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Table, Typography } from 'antd';
import styled from 'styled-components';
import ButtonIcon from 'components/buttonIcon';
import { deleteReportAction, patchReportAction } from 'contexts/reporting/actions';
import { formatCreatedBy, formatDateToLocaleSting, parseSize } from '../shared/utils';
import Notification from 'components/notification';
import { IReport } from 'contexts/reporting/types';
import { ReportsContext } from 'contexts/reporting';
import SortableHeader from 'components/Table/SortableHeader';
import { Pagination } from 'components/Table';
import {
  getColumnDirection,
  getNextSortState,
  loadSortState,
  saveSortState,
  toOrdering,
} from 'utils/sorting';
import { SortState, SortStorageKeyParts, UNSORTED } from 'types/sorting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import SpinnerIMG from 'assets/images/spinner.png';
import { Spin } from 'components/loadings/Spinner';
import { capitalize, formatDateInUtc } from 'utils/helpers';
import { ActionModal } from 'components/modal';
import { SessionContext } from 'contexts/session';
import { screenMd } from 'constants/styles/breakpoints';
import { isMobile } from 'react-device-detect';
import StatusCell from './StatusCell';
import ButtonsCell from './ButtonsCell';
import ReportingHistoryMobileList from './ReportingHistoryMobileList';
import InputText from '../InputText';
import InputSelectMultiple from 'components/inputs/inputSelectMultiple';
import InputSelect from 'components/inputs/inputSelect';
import { InputSelectMultipleWrapper } from 'pages/Reporting/components/styles';
import Icon from 'components/icons';
import {
  ACCOUNT,
  COURSES,
  DATE_ONLY_PATTERN,
  TEAM_ACTIVITY,
  USER_ACTIVITY,
} from 'constants/reporting';
import { getPopularReportImg } from 'utils/imageHandling';
const { Text } = Typography;

const HistoryTableContainer = styled.div(
  ({ theme }) => `
  position: relative;
  border-radius: 0 0 10px 10px;
  background: white;
  min-height: 600px;
  transition: min-height 1s ease; 
  padding: 10px;

  ${screenMd} {
    padding: 0;
  }

  .table-bar {
    &-container {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      margin: 0 0 20px;

      ${screenMd} {
        margin: 0 25px 15px 0;
        flex-wrap: nowrap;
      }

      h4 {
        display: flex;
        align-items: center;
        width: auto;
        flex: 1;
        gap: 4px;

        .filters-type {
          display: none;
        }
        .report {
          text-transform: capitalize;
        }
        ${screenMd} {
          margin-bottom: 0;
          .filters-type { 
            display: inline;
          }
          .report {
            text-transform: none;
          }
        }
      }
    }

    &-actions {
      display: flex;
      gap: 10px;
      align-items: center;
      flex: 0 0 auto;
      margin-left: auto;
      ${screenMd} {
        justify-content: flex-end;
        flex: 1;
        margin-left: 0;
      }
    }
  }

  .empty-state {
    display: flex;
    gap: 10px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 50vh;
    width: 100%;

    h2,
    h4 {
      margin: 0 !important;
    }

    & h4 {
      font-size: 20px;
      font-style: normal;
      font-weight: 450;
      line-height: 26px;
      color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
    }
  }

  .mobile-history-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin: 0 0 16px;
    font-size: 12px;
    font-weight: 700;
    color: var(--light-gray-darker);

    ${screenMd} {
      display: none;
    }

    &__title {
      font-size: 14px;
    }

    button {
      flex-shrink: 0;
    }
  }

  .ant-table {
    font-size: 14px;

    .ant-table-cell-fix-left,
    .ant-table-cell-fix-right {
      background-color: var(--white);
      z-index: 2;
    }

    .ant-table-thead {
      .ant-table-cell-fix-left,
      .ant-table-cell-fix-right {
        z-index: 3;
      }
    }

    .ant-table-thead {
      > tr {
        > th {
          color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
          background-color: transparent;
          border-bottom: none !important;

          padding: 12px 10px 15px 0;
          &:last-child {
            padding-right: 0;
            padding-left: 10px;
          }

          &::before {
            display: none !important;
          }

          &.ant-table-cell-fix-left,
          &.ant-table-cell-fix-right {
            background-color: var(--white);
          }
        }
      }
    }
    .ant-table-tbody {
      > tr {
        > td {
          padding: 10px 10px 10px 0;
          &:last-child {
            padding-right: 0;
            padding-left: 10px;
          }
        }

        &:last-child {
          > td {
            border-bottom: none;
          }
        }
      }
    }
  }

  .ant-table-ping-left .ant-table-cell-fix-left-last::after {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.04);
  }

  .ant-table-ping-right .ant-table-cell-fix-right-first::after {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.04);
  }

  .report-name {
    color: var(--black-dark);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    min-width: 0; 
  }

  .report-subtitle {
    overflow: hidden;
    color: var(--light-gray-darker);
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: var(--font-family-base);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
`
);

type ReportTypeFilter =
  | typeof USER_ACTIVITY
  | typeof TEAM_ACTIVITY
  | typeof ACCOUNT
  | typeof COURSES;

const HISTORY_SORT_KEY: SortStorageKeyParts = { table: 'reports', variant: 'history' };
const DEFAULT_HISTORY_SORT: SortState = { columnKey: 'created_at', direction: 'desc' };

const ReportingHistoryTable = (): JSX.Element => {
  const { fetching, reports, getReports, getReportCreators, reportCreators } =
    useContext(ReportsContext);
  const { isTeamManager, user } = useContext(SessionContext);
  const { dashboardReportingSorting } = useFlags() || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [updating, setUpdating] = useState(false);
  const loading = useMemo(() => fetching || updating, [fetching, updating]);
  const [reportToDelete, setReportToDelete] = useState<{ id: string; name: string } | null>(null);
  const [createdByIds, setCreatedByIds] = useState<string[]>([]);
  const [sortState, setSortState] = useState<SortState>(() => {
    if (!dashboardReportingSorting) return UNSORTED;
    const saved = loadSortState(HISTORY_SORT_KEY);
    return saved.columnKey ? saved : DEFAULT_HISTORY_SORT;
  });
  const ordering = toOrdering(sortState);
  const [typeFilter, setTypeFilter] = useState<ReportTypeFilter | null>(null);
  const [typeFilterLabel, setTypeFilterLabel] = useState<string>('');

  const handleSortToggle = useCallback(
    (columnKey: string) => {
      if (!dashboardReportingSorting) return;
      setSortState((prev) => {
        const next = getNextSortState(prev, columnKey);
        saveSortState(HISTORY_SORT_KEY, next);
        return next;
      });
      setCurrentPage(1);
    },
    [dashboardReportingSorting]
  );

  useEffect(() => {
    fetchReports();
  }, [ordering, currentPage, pageSize, createdByIds.join(','), typeFilter]);

  useEffect(() => {
    if (!isTeamManager) {
      getReportCreators();
    }
  }, []);

  const fetchReports = async () =>
    getReports(
      {
        ordering: ordering ?? undefined,
        created_by_ids: createdByIds,
        page: currentPage,
        page_size: pageSize,
      },
      true
    );

  const handleUpdate = async (id: string, key: string, value: string | boolean) => {
    setUpdating(true);
    try {
      await patchReportAction(id, { [key]: value });

      Notification({ text: 'Report name updated.', type: 'success' });

      await fetchReports();
    } catch (error) {
      Notification({
        text: 'Something went wrong while saving your changes. Please try again.',
        type: 'error',
      });
    } finally {
      setUpdating(false);
    }
  };

  const onDeleteReport = (id: string, name: string) => {
    setReportToDelete({ id, name });
  };

  const onCancelDelete = () => {
    setReportToDelete(null);
  };

  const onConfirmDelete = async () => {
    setUpdating(true);
    try {
      if (reportToDelete) {
        await deleteReportAction(reportToDelete.id);
        Notification({
          text: 'Report has been removed from your downloaded reports.',
          type: 'success',
        });
        fetchReports();
      }
    } catch (error) {
      Notification({
        text: 'Something went wrong while saving your changes. Please try again.',
        type: 'error',
      });
    } finally {
      setUpdating(false);
      onCancelDelete();
    }
  };

  const onPagesClickHandler = (value: string, action: string) => {
    switch (action) {
      case 'page':
        setCurrentPage(parseInt(value));
        break;
      case 'page_size':
        setPageSize(parseInt(value));
        break;
    }
  };

  const reportsCreatorsFormatted = useMemo(() => {
    return (
      reportCreators?.map((user) => ({
        label: user.name,
        value: user.id,
        key: user.id,
      })) || []
    );
  }, [reportCreators]);

  const columns = useMemo(
    () => [
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Report name"
              columnKey="name"
              direction={getColumnDirection(sortState, 'name')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Report name</span>
          ),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        fixed: 'left' as const,
        width: '39.15%',
        render: (text: string, report: IReport) => {
          const isNotCreatedByUser = report.created_by.uaa_id !== user.uaa_id;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {report.is_popular_report && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'rgba(167, 147, 246, 0.12)',
                  }}
                >
                  <img
                    src={getPopularReportImg(report.type)}
                    alt={`${report.type} popular report`}
                    width={36}
                    height={36}
                    style={{ display: 'inline-block' }}
                  />
                </span>
              )}
              <div style={{ flex: 1 }} className="report-name">
                {report.is_popular_report ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text ellipsis={{ tooltip: text }}>{text}</Text>
                    {(() => {
                      const anyReport = report as unknown as {
                        frequency?: string;
                        date_range?: {
                          from_date: string;
                          to_date: string;
                        };
                      };

                      const frequencyText = anyReport?.frequency
                        ? capitalize(anyReport.frequency)
                        : '';
                      const drSource = anyReport?.date_range || anyReport?.date_range || null;
                      const fromStr = drSource?.from_date ?? null;
                      const toStr = drSource?.to_date ?? null;

                      const formatRangeValue = (value?: string | null): string | null => {
                        if (!value) return null;
                        if (DATE_ONLY_PATTERN.test(value)) return value;
                        const parsed = new Date(value);
                        if (Number.isNaN(parsed.getTime())) return value;
                        return formatDateInUtc(parsed, 'yyyy-MM-dd');
                      };

                      const parts: string[] = [];
                      if (frequencyText) parts.push(frequencyText);
                      if (fromStr && toStr) {
                        const startFmt = formatRangeValue(fromStr);
                        const endFmt = formatRangeValue(toStr);
                        parts.push(`${startFmt} to ${endFmt}`);
                      }

                      if (!parts.length) return null;
                      return <span className="report-subtitle">{parts.join(': ')}</span>;
                    })()}
                  </div>
                ) : (
                  <InputText
                    tooltipPlacement="bottomLeft"
                    disabledTooltipText={
                      isNotCreatedByUser && 'Only the creator can edit the report name'
                    }
                    disabled={isNotCreatedByUser || loading}
                    value={text}
                    onSave={(newValue) => handleUpdate(report.id, 'name', newValue)}
                  />
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Type"
              columnKey="type"
              direction={getColumnDirection(sortState, 'type')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Type</span>
          ),
        dataIndex: 'type',
        key: 'type',
        width: '6%',
        render: (type: string | undefined | null) => <Text>{type ? capitalize(type) : ' - '}</Text>,
      },
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Size"
              columnKey="size"
              direction={getColumnDirection(sortState, 'size')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Size</span>
          ),
        dataIndex: 'size',
        key: 'size',
        width: '7.9%',
        render: (size: number) => <Text>{parseSize(size)}</Text>,
      },
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Created/Scheduled by"
              columnKey="created_by"
              direction={getColumnDirection(sortState, 'created_by')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Created/Scheduled by</span>
          ),
        dataIndex: 'created_by',
        key: 'created_by',
        width: '15.9%',
        render: (created_by: { first_name: string; last_name: string }, { scheduled }: IReport) => (
          <Text style={{ maxWidth: 200, display: 'inline-block' }}>
            {formatCreatedBy(created_by, scheduled)}
          </Text>
        ),
      },
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Date created"
              columnKey="created_at"
              direction={getColumnDirection(sortState, 'created_at')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Date created</span>
          ),
        dataIndex: 'created_at',
        key: 'created_at',
        width: '12%',
        render: (dateString: string) => {
          const formattedDate = formatDateToLocaleSting(new Date(dateString));
          return <Text>{formattedDate}</Text>;
        },
      },
      {
        title: () =>
          dashboardReportingSorting ? (
            <SortableHeader
              label="Download status"
              columnKey="status"
              direction={getColumnDirection(sortState, 'status')}
              onToggle={handleSortToggle}
              disabled={loading}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 14 }}>Download status</span>
          ),
        dataIndex: 'status',
        key: 'status',
        width: '14%',
        render: (status: string) => <StatusCell status={status} />,
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        fixed: 'right' as const,
        width: '7.15%',
        render: (id: string, report: IReport) => (
          <ButtonsCell report={report} loading={loading} onDeleteReport={onDeleteReport} />
        ),
      },
    ],
    [
      loading,
      handleUpdate,
      onDeleteReport,
      ordering,
      sortState,
      handleSortToggle,
      user.uaa_id,
      dashboardReportingSorting,
    ]
  );

  return (
    <HistoryTableContainer>
      <div className="table-bar-container">
        <Typography.Title level={4}>
          <span className="report">Report history</span>
        </Typography.Title>
        <div className="table-bar-actions">
          {!isMobile && (
            <>
              <InputSelectMultipleWrapper>
                <InputSelect
                  value={typeFilterLabel}
                  placeHolder="Filter by type"
                  options={[
                    { label: 'User', value: USER_ACTIVITY },
                    { label: 'Team', value: TEAM_ACTIVITY },
                    { label: 'Account', value: ACCOUNT },
                    { label: 'Course', value: COURSES },
                    // Keep "All types" as the last option
                    { label: 'All types', value: 'all' },
                  ]}
                  onChange={(v) => {
                    if (v === 'all') {
                      // Clear the type filter and show label
                      setTypeFilter(null);
                      setTypeFilterLabel('All types');
                      return;
                    }
                    const map = {
                      [USER_ACTIVITY]: 'User',
                      [TEAM_ACTIVITY]: 'Team',
                      [ACCOUNT]: 'Account',
                      [COURSES]: 'Course',
                    } as Record<ReportTypeFilter, string>;
                    const value = v as ReportTypeFilter;
                    setTypeFilter(value);
                    setTypeFilterLabel(map[value] || '');
                  }}
                />
              </InputSelectMultipleWrapper>
              {!isTeamManager && (
                <InputSelectMultipleWrapper>
                  <InputSelectMultiple
                    className="multiple-select__filter-content"
                    customClassName="certification-filter"
                    showArrow={true}
                    suffixIcon={<Icon icon={'ChevronDown'} size={'smaller'} />}
                    values={createdByIds}
                    placeholder="Filter by creator"
                    options={reportsCreatorsFormatted}
                    reseteable={true}
                    optionFilterProp="label"
                    onChange={(v) => setCreatedByIds(v)}
                  />
                </InputSelectMultipleWrapper>
              )}
            </>
          )}
          <ButtonIcon
            $secondary
            size="small"
            icon="Refresh"
            disabled={loading}
            onClick={fetchReports}
          />
        </div>
      </div>
      {loading && <Spin zIndex="200" src={SpinnerIMG} size="80" />}
      {reports?.results?.length ? (
        <>
          {isMobile ? (
            <>
              <div className="mobile-history-header">
                {dashboardReportingSorting ? (
                  <SortableHeader
                    label="Report name"
                    columnKey="name"
                    direction={getColumnDirection(sortState, 'name')}
                    onToggle={handleSortToggle}
                    disabled={loading}
                  />
                ) : (
                  <span className="mobile-history-header__title">Report name</span>
                )}
              </div>
              <ReportingHistoryMobileList
                loading={loading}
                reports={reports}
                onDeleteReport={onDeleteReport}
                onUpdateReport={handleUpdate}
              />
            </>
          ) : (
            <Table
              style={{ opacity: loading ? 0.5 : 1 }}
              rowKey="id"
              rowClassName="fw-bold"
              columns={columns}
              dataSource={reports.results}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          )}
          <Pagination
            count={reports.count}
            page={currentPage}
            page_size={pageSize}
            onPagesClick={onPagesClickHandler}
          />
        </>
      ) : (
        !loading && (
          <div className="empty-state">
            <Typography.Title level={2}>Sorry, we couldn’t find any matches.</Typography.Title>
            <Typography.Title level={4}>
              Please try searching with a different set of filters.
            </Typography.Title>
          </div>
        )
      )}
      <ActionModal
        title="Remove Report"
        description={`Are you sure you want to remove "${reportToDelete?.name}" from your report history?`}
        active={!!reportToDelete}
        buttons={[
          { label: 'Cancel', onClick: onCancelDelete, type: 'secondary' },
          { label: 'Remove report', onClick: onConfirmDelete, type: 'primary' },
        ]}
        handleCancel={onCancelDelete}
      />
    </HistoryTableContainer>
  );
};

export default ReportingHistoryTable;
